import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import { getPricePerPixel, getNextTierInfo } from '../utils/pricing';
const { FiZap, FiX, FiArrowRight, FiStar, FiClock, FiShield, FiTrendingUp, FiCheck } = FiIcons;

const TESTIMONIALS = [
  { name: 'Alex K.', role: 'Founder @ LaunchClub', text: 'Got 3 backlinks and 200 referral visitors in week one. Best $20 I ever spent.' },
  { name: 'Priya S.', role: 'SEO Lead @ Webflow', text: 'The dofollow link alone was worth it. DA boost was real.' },
  { name: 'Tomas R.', role: 'Indie Hacker', text: "My pixel is still there. It will be there in 10 years. That's insane value." },
];

export default function WelcomeModal() {
  const { isWelcomeModalOpen, setIsWelcomeModalOpen, pixelsSold, liveVisitors, setMode, tiles } = useAppContext();
  const [count, setCount] = useState(0);
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [secs, setSecs] = useState(0);
  const nextTier = getNextTierInfo(pixelsSold);
  const price = getPricePerPixel(pixelsSold);
  const percentSold = ((pixelsSold / 1000000) * 100).toFixed(2);

  const topTiles = [...tiles].filter(t => t.title && t.link).sort((a, b) => (b.w * b.h) - (a.w * a.h)).slice(0, 5);
  const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32', '#ff00cc', '#ff00cc'];

  useEffect(() => {
    if (!isWelcomeModalOpen) return;
    let start = 0;
    const step = pixelsSold / (1200 / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= pixelsSold) { setCount(pixelsSold); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [isWelcomeModalOpen, pixelsSold]);

  useEffect(() => {
    const t = setInterval(() => setTestimonialIdx(i => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const now = Math.floor(Date.now() / 1000);
    const cycle = 15 * 60;
    setSecs(cycle - (now % cycle));
    const t = setInterval(() => setSecs(s => s <= 1 ? cycle : s - 1), 1000);
    return () => clearInterval(t);
  }, []);

  if (!isWelcomeModalOpen) return null;

  const handleClaim = () => { setIsWelcomeModalOpen(false); setMode('select'); };
  const handleClose = () => setIsWelcomeModalOpen(false);
  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');
  const tm = TESTIMONIALS[testimonialIdx];

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/85 backdrop-blur-md"
      onClick={handleClose}>
      <div className="relative w-full sm:max-w-[500px] animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className="absolute -inset-[1.5px] rounded-t-2xl sm:rounded-2xl"
          style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff,#ff00cc)' }} />
        <div className="relative rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
          style={{ background: '#080012', maxHeight: '92vh' }}>
          <div className="absolute inset-0 scanline pointer-events-none z-10" />

          {/* Drag handle (mobile) */}
          <div className="flex justify-center pt-3 pb-1 sm:hidden">
            <div className="w-10 h-1 rounded-full bg-gray-700" />
          </div>

          <button onClick={handleClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white transition-all flex items-center justify-center z-30">
            <FiX className="text-lg" />
          </button>

          <div className="overflow-y-auto flex-1 px-5 pt-4 pb-6">
            {/* Live badge */}
            <div className="flex justify-center mb-3">
              <div className="inline-flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 text-pink-300 font-bold px-4 py-1.5 rounded-full uppercase tracking-widest" style={{ fontSize: '12px' }}>
                <span className="w-2 h-2 rounded-full bg-pink-500 animate-blink" />
                Live — {liveVisitors} browsing now
              </div>
            </div>

            {/* Countdown */}
            <div className="flex items-center justify-center gap-2 mb-3 px-4 py-2 rounded-xl"
              style={{ background: 'rgba(255,60,0,0.1)', border: '1px solid rgba(255,100,0,0.3)' }}>
              <FiClock className="text-orange-400 text-sm animate-blink" />
              <span className="text-orange-300 font-bold" style={{ fontSize: '13px' }}>Price lock expires in </span>
              <span className="text-white font-black font-mono" style={{ fontSize: '15px' }}>{mm}:{ss}</span>
            </div>

            {/* Title */}
            <div className="text-center mb-4">
              <h1 className="font-black text-glow-pink leading-none" style={{ color: '#ff00cc', fontFamily: 'Impact,sans-serif', letterSpacing: '3px', fontSize: 'clamp(28px,8vw,52px)' }}>
                1 MILLION PIXELS
              </h1>
              <p className="text-gray-300 mt-2 leading-relaxed" style={{ fontSize: '15px' }}>
                Immortalize your brand forever. <span className="text-pink-400 font-bold">Price rises every tier.</span>
              </p>
            </div>

            {/* CTA */}
            <button onClick={handleClaim}
              className="w-full py-4 rounded-xl font-black uppercase tracking-widest text-white transition-all animate-pulse-pink hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 mb-4"
              style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)', fontFamily: 'Impact,sans-serif', letterSpacing: '3px', fontSize: '18px' }}>
              <FiZap className="text-xl" /> CLAIM PIXELS <FiArrowRight className="text-xl" />
            </button>

            {/* Trust row */}
            <div className="flex items-center justify-center gap-4 mb-4 flex-wrap">
              {[
                { icon: FiShield, text: 'Permanent' },
                { icon: FiTrendingUp, text: 'Price rising' },
                { icon: FiCheck, text: 'SEO backlink' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-1.5 text-gray-400" style={{ fontSize: '12px' }}>
                  <item.icon className="text-pink-500 text-xs" /> {item.text}
                </div>
              ))}
            </div>

            {/* Tier warning */}
            {nextTier && (
              <div className="mb-4 p-3 rounded-xl border border-pink-500/30" style={{ background: 'rgba(255,0,200,0.05)' }}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-pink-400 font-black uppercase tracking-wider flex items-center gap-1" style={{ fontSize: '12px' }}>
                    <FiZap className="text-xs" /> PRICE JUMPS SOON
                  </span>
                  <span className="text-white font-black" style={{ fontSize: '14px' }}>${price.toFixed(2)}/px</span>
                </div>
                <p className="text-gray-300" style={{ fontSize: '13px' }}>
                  <span className="text-pink-300 font-black">{nextTier.remaining.toLocaleString()}</span> more pixels until →{' '}
                  <span className="text-pink-300 font-black">${nextTier.nextPrice.toFixed(2)}/px</span>
                </p>
                <div className="mt-2 h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${percentSold}%`, background: 'linear-gradient(90deg,#ff00cc,#cc00ff)' }} />
                </div>
              </div>
            )}

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { label: 'Price/px', value: `$${price.toFixed(2)}`, color: 'white' },
                { label: 'Sold', value: `${percentSold}%`, color: '#ff00cc' },
                { label: 'Unique Owners', value: `${tiles.filter(t => t.title && t.link).length}+`, color: 'white' },
                { label: 'Pixels Sold', value: count.toLocaleString(), color: '#ff00cc' },
              ].map((s, i) => (
                <div key={i} className="rounded-xl p-3 text-center"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="text-gray-400 uppercase tracking-wider mb-1" style={{ fontSize: '11px' }}>{s.label}</div>
                  <div className="font-black" style={{ color: s.color, fontSize: '20px' }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Testimonial */}
            <div className="mb-4 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,0,200,0.15)' }}>
              <div className="text-gray-300 italic leading-relaxed mb-2" style={{ fontSize: '13px' }}>"{tm.text}"</div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center font-black text-xs"
                  style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)' }}>
                  {tm.name[0]}
                </div>
                <div>
                  <div className="text-white font-bold" style={{ fontSize: '13px' }}>{tm.name}</div>
                  <div className="text-gray-500" style={{ fontSize: '11px' }}>{tm.role}</div>
                </div>
                <div className="ml-auto flex gap-0.5">
                  {[...Array(5)].map((_, i) => <FiStar key={i} className="text-yellow-400 text-xs" />)}
                </div>
              </div>
            </div>

            {/* Top Advertisers */}
            <div>
              <div className="text-pink-400 font-black uppercase tracking-widest mb-2 flex items-center gap-2" style={{ fontSize: '12px' }}>
                <FiStar /> TOP ADVERTISERS
              </div>
              <div className="space-y-1.5">
                {topTiles.map((tile, i) => (
                  <div key={tile.id} className="flex items-center justify-between px-3 py-2 rounded-lg"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center font-black shrink-0"
                        style={{ background: `${rankColors[i]}20`, color: rankColors[i], fontSize: '13px' }}>{i + 1}</span>
                      <div className="w-6 h-6 rounded overflow-hidden shrink-0 border border-gray-800"
                        style={{ background: tile.solidColor || '#1a0030' }}>
                        {tile.image ? <img src={tile.image} alt={tile.title} className="w-full h-full object-cover" /> : null}
                      </div>
                      <span className="text-gray-200 font-bold" style={{ fontSize: '14px' }}>{tile.title}</span>
                    </div>
                    <span className="font-black" style={{ color: rankColors[i], fontSize: '13px' }}>
                      {(tile.w * tile.h).toLocaleString()} px
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}